import Datepicker from '@/components/Datepicker.vue';
import '@vuepic/vue-datepicker/dist/main.css';
import 'mosha-vue-toastify/dist/style.css';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import './datepicker.scss';
import 'vue-final-modal/style.css';

import Multiselect from '@vueform/multiselect';
import App from './App.vue';
import { i18n } from './i18n';
import { vueQueryPluginOptions } from './queries/query-client';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import router from './router';
import { createVfm, VueFinalModal } from 'vue-final-modal';
import { formatCurrency } from './utils/format-numbers';
import { formatDate } from './hooks/use-formatted-date';
import CVButton from './components/buttons/CVButton.vue';
import Icon from './components/icons/Icon.vue';
import Spinner from './components/icons/Spinner.vue';
import PrimeVue from 'primevue/config';
import * as Sentry from '@sentry/vue';
import { abilitiesPlugin } from '@casl/vue';
import { aliasResolver } from './entities/auth/abilities';
import { createMongoAbility } from '@casl/ability';
import { configure } from 'vee-validate';
import { Capacitor } from '@capacitor/core';

configure({
  validateOnBlur: true,
  validateOnChange: false,
  validateOnInput: false,
  validateOnModelUpdate: false,
});

const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [/^https:\/\/.*\.carvia\.de/],
    }),
  ],
  tracesSampleRate: 0.05,
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/api\.carvia\.de/,
    /^https:\/\/ros-api-stage\.herokuapp\.com/,
  ],
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    'partial-range', // thrown by vue datepicker, but meaningless
    // Stale chunks after a deploy, fixed by reloading
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
  ],
  beforeSend(event, hint) {
    if (event.extra?.forceReport) return event;
    if (
      event.exception?.values?.[0]?.type === 'AxiosError' &&
      (event.exception.values[0].value === 'Network Error' ||
        // Client errors are shown to the user; server errors are already reported by the backend
        /^Request failed with status code \d{3}$/.test(event.exception.values[0].value ?? '') ||
        event.exception.values[0].value?.startsWith('timeout of'))
    ) {
      return null;
    }
    if (
      event.exception?.values?.[0]?.type === 'AbortError' &&
      event.exception.values[0].value === 'Fetch is aborted'
    ) {
      return null;
    }
    // Network failures: Safari reports "Load failed", Chromium "Failed to fetch"
    if (['Load failed', 'Failed to fetch'].includes(event.exception?.values?.[0].value ?? '')) {
      return null;
    }
    return event;
  },
});

if (Capacitor.isNativePlatform()) {
  document.documentElement.style.overflowX = 'hidden';
  document.body.style.overflowX = 'hidden';
}

app.component('Datepicker', Datepicker);
app.component('VueFinalModal', VueFinalModal);
app.component('Multiselect', Multiselect);
app.component('PulseLoader', PulseLoader);
app.component('CVButton', CVButton);
app.component('Icon', Icon);
app.component('Spinner', Spinner);

app.use(i18n);
app.use(createPinia());
app.use(router);

app.use(VueQueryPlugin, vueQueryPluginOptions);
const vfm = createVfm();
app.use(vfm);
app.use(PrimeVue);

app.directive('currency', (el, binding) => {
  el.textContent = formatCurrency(binding.value);
});

app.directive('date', (el, binding) => {
  el.textContent = formatDate(binding.value.value, binding.value.format);
});

export const ability = createMongoAbility([], { resolveAction: aliasResolver });

app.use(
  abilitiesPlugin,
  ability, // init with empty ability
  {
    useGlobalProperties: true,
  },
);

app.mount('#app');
